import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import CopyStudentNameButton from '../../components/common/CopyStudentNameButton/CopyStudentNameButton';
import DataTable from '../../components/common/DataTable/DataTable';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FiltersGroup from '../../components/common/FiltersGroup/FiltersGroup';
import FormInput from '../../components/common/FormInput/FormInput';
import FormTextarea from '../../components/common/FormTextarea/FormTextarea';
import Loading from '../../components/common/Loading/Loading';
import PageContainer, { PageBody } from '../../components/common/PageContainer/PageContainer';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../components/common/Pagination/Pagination';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import ToolbarRow from '../../components/common/ToolbarRow/ToolbarRow';
import { usePageSize } from '../../hooks/usePageSize';
import { useQueryFilters } from '../../hooks/useQueryFilters';
import { useWorkoutDeliveryManagement } from '../../hooks/useWorkoutDeliveryManagement';
import { WorkoutAssignmentService } from '../../services';
import type { TrainingItem } from '../../types/training';
import type { WorkoutDeliveryFilters, WorkoutDeliveryRow } from '../../types/workoutDeliveryManagement';
import TrainingCatalogSearchModal from '../Performance/components/TrainingCatalogSearchModal';
import { WORKOUT_DELIVERY_QUERY_FILTER_SCHEMA } from './workoutDeliveryQueryFilters';
import styles from './WorkoutDeliveryManagement.module.css';

type CatalogTarget = 'current' | 'next';

type RowDraft = {
  currentTrainingCode: string;
  nextTrainingCode: string;
  expectedDeliveryDate: string;
  deliveredDate: string;
  note: string;
};

const toDateOnly = (value: string | null | undefined): string => {
  const date = value?.slice(0, 10) ?? '';
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : '';
};

const draftFromRow = (row: WorkoutDeliveryRow): RowDraft => ({
  currentTrainingCode: (row.current_training_code ?? '').toUpperCase(),
  nextTrainingCode: (row.next_training_code ?? '').toUpperCase(),
  expectedDeliveryDate: toDateOnly(row.expected_delivery_date),
  deliveredDate: toDateOnly(row.delivered_at),
  note: '',
});

const isDraftDirty = (row: WorkoutDeliveryRow, draft: RowDraft): boolean => {
  const baseline = draftFromRow(row);
  return draft.currentTrainingCode !== baseline.currentTrainingCode
    || draft.nextTrainingCode !== baseline.nextTrainingCode
    || draft.expectedDeliveryDate !== baseline.expectedDeliveryDate
    || draft.deliveredDate !== baseline.deliveredDate
    || draft.note.trim().length > 0;
};

const WorkoutDeliveryManagementPage: React.FC = () => {
  const {
    filters: queryFilters,
    setFilter,
  } = useQueryFilters(WORKOUT_DELIVERY_QUERY_FILTER_SCHEMA, { pageKey: 'page' });

  const { searchName } = queryFilters;

  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('workout_delivery_page_size');

  const filters: WorkoutDeliveryFilters = useMemo(
    () => ({ searchName: searchName || undefined }),
    [searchName],
  );

  const {
    rows,
    pagination,
    loading,
    error,
    refresh,
    removeRow,
    patchRow,
  } = useWorkoutDeliveryManagement(page, pageSize, filters);

  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({});
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [successMessage, setSuccessMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [catalogTarget, setCatalogTarget] = useState<{
    studentId: number;
    field: CatalogTarget;
  } | null>(null);

  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<number, RowDraft> = {};
      rows.forEach((row) => {
        next[row.student_id] = prev[row.student_id] ?? draftFromRow(row);
      });
      return next;
    });
  }, [rows]);

  const updateDraft = useCallback((studentId: number, patch: Partial<RowDraft>) => {
    setDrafts((prev) => {
      const current = prev[studentId];
      if (!current) return prev;
      return { ...prev, [studentId]: { ...current, ...patch } };
    });
  }, []);

  const handleSave = useCallback(async (row: WorkoutDeliveryRow) => {
    const draft = drafts[row.student_id];
    if (!draft || savingIds.has(row.student_id)) return;

    const note = draft.note.trim();
    const noteToSave = note.length >= 5 ? note : undefined;

    setSavingIds((prev) => new Set(prev).add(row.student_id));
    setSaveError('');
    setSuccessMessage('');

    try {
      const saved = await WorkoutAssignmentService.updateDeliveryManagement({
        assignmentId: row.assignment_id,
        studentId: row.student_id,
        currentTrainingCode: draft.currentTrainingCode.trim() || null,
        nextTrainingCode: draft.nextTrainingCode.trim() || null,
        expectedDeliveryDate: draft.expectedDeliveryDate || null,
        deliveredDate: draft.deliveredDate || null,
        note: noteToSave,
      });

      if (saved.delivered_at) {
        removeRow(row.student_id);
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[row.student_id];
          return next;
        });
        setSuccessMessage('Entrega salva. A aluna saiu da listagem.');
        if (rows.length <= 1 && page > 1) {
          handlePageChange(page - 1);
        } else {
          refresh({ silent: true });
        }
      } else {
        patchRow(row.student_id, {
          assignment_id: saved.assignment_id,
          assignment_status: row.assignment_status,
          current_training_code: saved.current_training_code,
          next_training_code: saved.next_training_code,
          expected_delivery_date: saved.expected_delivery_date,
          delivered_at: saved.delivered_at,
        });
        setDrafts((prev) => ({
          ...prev,
          [row.student_id]: {
            currentTrainingCode: (saved.current_training_code ?? '').toUpperCase(),
            nextTrainingCode: (saved.next_training_code ?? '').toUpperCase(),
            expectedDeliveryDate: toDateOnly(saved.expected_delivery_date),
            deliveredDate: toDateOnly(saved.delivered_at),
            note: '',
          },
        }));
        setSuccessMessage('Entrega atualizada com sucesso.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Não foi possível salvar a entrega.';
      setSaveError(message);
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.student_id);
        return next;
      });
    }
  }, [drafts, handlePageChange, page, patchRow, refresh, removeRow, rows.length, savingIds]);

  const handleCatalogSelect = useCallback((training: TrainingItem) => {
    if (!catalogTarget) return;
    const code = (training.code?.trim() || training.name.trim()).toUpperCase();
    if (!code) {
      setSaveError('Este treino não possui código.');
      setCatalogTarget(null);
      return;
    }
    updateDraft(catalogTarget.studentId, catalogTarget.field === 'current'
      ? { currentTrainingCode: code }
      : { nextTrainingCode: code });
    setCatalogTarget(null);
  }, [catalogTarget, updateDraft]);

  if (error) {
    return <EmptyState message={`Erro: ${error}`} />;
  }

  return (
    <PageContainer className={styles.page}>
      <ToolbarRow className={styles['toolbar-row']}>
        <RecordCount label="Entregas pendentes:" count={pagination.totalRecords} />
        <FiltersGroup>
          <SearchFilterBar
            searchValue={searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar aluna"
            loading={loading}
          />
        </FiltersGroup>
      </ToolbarRow>

      {successMessage && (
        <AlertBanner variant="success" message={successMessage} className={styles.alert} />
      )}
      {saveError && (
        <AlertBanner variant="error" message={saveError} className={styles.alert} />
      )}

      <PageBody className={styles['list-body']}>
        {loading && rows.length === 0 ? (
          <Loading />
        ) : rows.length === 0 ? (
          <EmptyState
            message={searchName
              ? 'Nenhuma aluna encontrada com o nome informado.'
              : 'Nenhuma aluna pendente de entrega de treino.'}
          />
        ) : (
          <DataTable
            headers={[
              'Nome da aluna',
              'Treino anterior',
              'Próximo treino',
              'Data prevista',
              'Data de entrega',
              'Observações',
              'Ações',
            ]}
            gridTemplate="minmax(150px, 1.3fr) minmax(160px, 1.1fr) minmax(160px, 1.1fr) minmax(130px, 0.9fr) minmax(130px, 0.9fr) minmax(160px, 1.2fr) 110px"
            highlightTerm={searchName}
          >
            {rows.map((row) => {
              const draft = drafts[row.student_id] ?? draftFromRow(row);
              const isSaving = savingIds.has(row.student_id);
              const dirty = isDraftDirty(row, draft);

              return (
                <DataTable.Row key={row.student_id}>
                  <div className={styles['student-name-row']}>
                    <a
                      className={styles['student-name']}
                      href={`/alunas/${row.student_id}/editar`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.student_name}
                    </a>
                    <CopyStudentNameButton studentName={row.student_name} />
                  </div>

                  <div className={styles['code-row']}>
                    <FormInput
                      id={`delivery-current-${row.student_id}`}
                      value={draft.currentTrainingCode}
                      onChange={(event) => updateDraft(row.student_id, {
                        currentTrainingCode: event.target.value.toUpperCase(),
                      })}
                      placeholder="Ex.: B07"
                      disabled={isSaving}
                      containerClassName={styles['code-input']}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="small"
                      className={styles['search-button']}
                      aria-label={`Buscar treino anterior de ${row.student_name}`}
                      title="Buscar treino anterior"
                      disabled={isSaving}
                      icon="search"
                      onClick={() => setCatalogTarget({
                        studentId: row.student_id,
                        field: 'current',
                      })}
                    />
                  </div>

                  <div className={styles['code-row']}>
                    <FormInput
                      id={`delivery-next-${row.student_id}`}
                      value={draft.nextTrainingCode}
                      onChange={(event) => updateDraft(row.student_id, {
                        nextTrainingCode: event.target.value.toUpperCase(),
                      })}
                      placeholder="Ex.: B08"
                      disabled={isSaving}
                      containerClassName={styles['code-input']}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="small"
                      className={styles['search-button']}
                      aria-label={`Buscar próximo treino de ${row.student_name}`}
                      title="Buscar próximo treino"
                      disabled={isSaving}
                      icon="search"
                      onClick={() => setCatalogTarget({
                        studentId: row.student_id,
                        field: 'next',
                      })}
                    />
                  </div>

                  <FormInput
                    id={`delivery-expected-${row.student_id}`}
                    type="date"
                    value={draft.expectedDeliveryDate}
                    onChange={(event) => updateDraft(row.student_id, {
                      expectedDeliveryDate: event.target.value,
                    })}
                    disabled={isSaving}
                    containerClassName={styles['date-input']}
                  />

                  <FormInput
                    id={`delivery-delivered-${row.student_id}`}
                    type="date"
                    value={draft.deliveredDate}
                    onChange={(event) => updateDraft(row.student_id, {
                      deliveredDate: event.target.value,
                    })}
                    disabled={isSaving}
                    containerClassName={styles['date-input']}
                  />

                  <FormTextarea
                    id={`delivery-notes-${row.student_id}`}
                    value={draft.note}
                    onChange={(event) => updateDraft(row.student_id, {
                      note: event.target.value,
                    })}
                    placeholder="Observação (opcional)"
                    disabled={isSaving}
                    minRows={2}
                    maxRows={4}
                    containerClassName={styles['notes-input']}
                  />

                  <Button
                    type="button"
                    variant="primary"
                    size="small"
                    disabled={!dirty || isSaving}
                    onClick={() => void handleSave(row)}
                  >
                    {isSaving ? 'Salvando…' : 'Salvar'}
                  </Button>
                </DataTable.Row>
              );
            })}
          </DataTable>
        )}
      </PageBody>

      {rows.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <TrainingCatalogSearchModal
        open={catalogTarget != null}
        onCancel={() => setCatalogTarget(null)}
        onSelect={handleCatalogSelect}
      />
    </PageContainer>
  );
};

export default WorkoutDeliveryManagementPage;
